import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
    Box,
    Drawer,
    DrawerContent,
    DrawerOverlay,
    Flex,
    Icon,
    IconButton,
    Text,
    Tooltip,
    useBreakpointValue,
} from '@chakra-ui/react'
import { useLocation } from '@reach/router'
import {
    FaAnglesRight,
    FaPlus,
    FaTableColumns,
    FaWindowRestore,
} from 'react-icons/fa6'

import { useClinic } from '~contexts/clinic-context'
import { HEADER_HEIGHT } from '~components/shared/layout/header/header-consts'
import {
    ASK_CHCKVET_MAX_WIDTH,
    ASK_CHCKVET_MIN_WIDTH,
    useAskChckvetDock,
} from './ask-chckvet-context'
import AskChckvetSessionMenu from './ask-chckvet-session-menu'
import AskChckvetThread from './ask-chckvet-thread'
import { ASK_CHCKVET_DOCK_ATTR } from './ask-chckvet-dom'
import { pageLabelFromPathname } from './clinic-agent-ui-context'
import {
    askChckvetMascotLabel,
    MascotAvatar,
    MascotEditorButton,
    useClinicMascot,
} from './mascot'

/**
 * Persistent Ask Chckvet work surface.
 *
 * On desktop it docks beside the page (content shrinks rather than being
 * covered) so staff can keep a thread open while they work; on smaller
 * screens there isn't room to split, so it falls back to an overlay drawer.
 */
export default function AskChckvetDock() {
    const { isOpen, width, displayMode } = useAskChckvetDock()
    const isMobile = useBreakpointValue({ base: true, lg: false }) ?? false

    if (!isOpen) return null

    if (isMobile) {
        return <AskChckvetMobileDrawer />
    }

    const isOverlay = displayMode === 'overlay'

    return (
        <Flex
            as="aside"
            aria-label="Ask Chckvet"
            direction="column"
            w={`${width}px`}
            flexShrink={0}
            bg="white"
            borderLeftWidth={1}
            borderColor="gray.200"
            minH={0}
            {...{ [ASK_CHCKVET_DOCK_ATTR]: '' }}
            {...(isOverlay
                ? {
                      // Float above the page instead of narrowing it. Starts
                      // below the header so the account menu and the Ask
                      // toggle stay reachable, and sits under Chakra's modal
                      // layer (1400) so dialogs still win.
                      position: 'fixed' as const,
                      top: `${HEADER_HEIGHT}px`,
                      right: 0,
                      bottom: 0,
                      zIndex: 1200,
                      shadow: '2xl',
                  }
                : { position: 'relative' as const })}
        >
            <ResizeHandle />
            <AskChckvetPanelContent />
        </Flex>
    )
}

function AskChckvetMobileDrawer() {
    const { isOpen, close } = useAskChckvetDock()

    return (
        <Drawer isOpen={isOpen} placement="right" onClose={close} size="full">
            <DrawerOverlay />
            <DrawerContent>
                <Flex
                    direction="column"
                    h="100%"
                    minH={0}
                    {...{ [ASK_CHCKVET_DOCK_ATTR]: '' }}
                >
                    <AskChckvetPanelContent />
                </Flex>
            </DrawerContent>
        </Drawer>
    )
}

function AskChckvetPanelContent() {
    const { clinic } = useClinic()
    const { close, startNewChat, displayMode, setDisplayMode } =
        useAskChckvetDock()
    const { mascot } = useClinicMascot()
    const location = useLocation()
    const pageLabel = pageLabelFromPathname(location.pathname)
    const isOverlay = displayMode === 'overlay'
    const title = askChckvetMascotLabel(mascot)

    return (
        <>
            <Box borderBottomWidth={1} borderColor="gray.200" px={3} py={2}>
                <Flex align="center" gap={2}>
                    <MascotAvatar mascot={mascot} size={28} />
                    <Text fontWeight="semibold" fontSize="sm" noOfLines={1}>
                        {title}
                    </Text>
                    <Flex ml="auto" align="center" gap={1}>
                        <MascotEditorButton />
                        <AskChckvetSessionMenu />
                        <Tooltip
                            label={
                                isOverlay
                                    ? 'Dock beside the page'
                                    : 'Float over the page'
                            }
                            openDelay={400}
                        >
                            <IconButton
                                aria-label={
                                    isOverlay
                                        ? 'Dock Ask Chckvet beside the page'
                                        : 'Float Ask Chckvet over the page'
                                }
                                icon={
                                    <Icon
                                        as={
                                            isOverlay
                                                ? FaTableColumns
                                                : FaWindowRestore
                                        }
                                    />
                                }
                                size="sm"
                                variant="ghost"
                                onClick={() =>
                                    setDisplayMode(
                                        isOverlay ? 'dock' : 'overlay',
                                    )
                                }
                            />
                        </Tooltip>
                        <Tooltip label="New chat" openDelay={400}>
                            <IconButton
                                aria-label="New chat"
                                icon={<Icon as={FaPlus} />}
                                size="sm"
                                variant="ghost"
                                onClick={startNewChat}
                            />
                        </Tooltip>
                        <Tooltip label="Close" openDelay={400}>
                            <IconButton
                                aria-label="Close Ask Chckvet"
                                icon={<Icon as={FaAnglesRight} />}
                                size="sm"
                                variant="ghost"
                                onClick={close}
                            />
                        </Tooltip>
                    </Flex>
                </Flex>
                <Flex align="center" gap={1.5} mt={1} minW={0}>
                    <Text fontSize="xs" color="gray.500" noOfLines={1}>
                        {clinic?.name ? `${clinic.name} · ` : ''}
                        {pageLabel}
                    </Text>
                </Flex>
            </Box>
            <AskChckvetThread />
        </>
    )
}

/**
 * Drag the dock wider/narrower from its left edge. Width is persisted by the
 * dock context.
 */
function ResizeHandle() {
    const { width, setWidth } = useAskChckvetDock()
    const [isDragging, setIsDragging] = useState(false)
    const widthRef = useRef(width)

    useEffect(() => {
        widthRef.current = width
    }, [width])

    const handlePointerDown = useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            event.preventDefault()
            setIsDragging(true)

            const onPointerMove = (moveEvent: PointerEvent) => {
                // Dock is right-aligned, so width grows as the pointer moves left.
                setWidth(window.innerWidth - moveEvent.clientX)
            }
            const onPointerUp = () => {
                setIsDragging(false)
                window.removeEventListener('pointermove', onPointerMove)
                window.removeEventListener('pointerup', onPointerUp)
            }

            window.addEventListener('pointermove', onPointerMove)
            window.addEventListener('pointerup', onPointerUp)
        },
        [setWidth],
    )

    return (
        <Box
            role="separator"
            aria-label="Resize Ask Chckvet"
            aria-orientation="vertical"
            aria-valuenow={width}
            aria-valuemin={ASK_CHCKVET_MIN_WIDTH}
            aria-valuemax={ASK_CHCKVET_MAX_WIDTH}
            position="absolute"
            left="-3px"
            top={0}
            bottom={0}
            w="6px"
            zIndex={1}
            cursor="col-resize"
            onPointerDown={handlePointerDown}
            _hover={{ bg: 'brand.200' }}
            bg={isDragging ? 'brand.300' : 'transparent'}
            transition="background 120ms ease"
        />
    )
}
